<script setup>
import { ref, onMounted } from 'vue';
import { i18n } from '@/i18n';
import { getUser, getUserAsync } from '@/lib/user';
import { useToast } from '@/components/ui/toast/use-toast';
import { request } from '@/lib/api';

const { toast } = useToast();
const isResending = ref(false);
const resendSuccess = ref(false);
const email = ref('');

const props = defineProps({
  show: Boolean,
});

const emit = defineEmits(['update:show']);

onMounted(() => {
  const user = getUser();
  if (user && user.email) {
    email.value = user.email;
  }
});

async function resendVerificationEmail() {
  if (isResending.value) return;

  isResending.value = true;
  resendSuccess.value = false;

  try {
    const language = i18n.language;
    const res = await request(`/api/send-verification-email?language=${language}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (res.verified) {
      await getUserAsync();
      closeModal();
    } else {
      resendSuccess.value = true;
      toast({
        title: i18n.get('auth.verificationEmailSent'),
        description: i18n.get('auth.checkInbox'),
        variant: 'success',
      });
    }
  } catch (error) {
    toast({
      title: i18n.get('auth.verificationEmailFailed'),
      description: `${i18n.get('auth.tryAgainLater')} ${error.message}`,
      variant: 'destructive',
    });
  } finally {
    isResending.value = false;
  }
}

function closeModal() {
  emit('update:show', false);
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
      @click="closeModal"
    >
      <div
        class="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200"
        @click.stop
      >
        <div class="flex flex-col items-center text-center">
          <div class="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-8 w-8 text-amber-500"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clip-rule="evenodd"
              />
            </svg>
          </div>

          <h2 class="text-2xl font-bold text-black/90 mb-2">
            {{ i18n.get('auth.emailVerificationRequired') }}
          </h2>

          <p class="text-black/60 mb-6">
            {{ i18n.get('auth.verificationDescription') }}
            <span class="font-medium text-black/90">{{ email }}</span>
          </p>

          <div class="w-full space-y-3">
            <button
              @click="resendVerificationEmail"
              class="w-full py-2.5 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              :disabled="isResending"
            >
              <svg
                v-if="isResending"
                class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {{
                isResending ? i18n.get('auth.sending') : i18n.get('auth.resendVerificationEmail')
              }}
            </button>

            <div v-if="resendSuccess" class="text-sm text-green-600 mt-2">
              {{ i18n.get('auth.verificationEmailSent') }}
            </div>

            <button
              @click="closeModal"
              class="w-full py-2.5 px-4 rounded-lg border border-black/20 bg-white hover:bg-black/5 text-black/70 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {{ i18n.get('auth.close') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
